import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { makeStyles } from "@material-ui/core";
import ProfitLossDateRangePicker from "../../../Shared/DateSelection/ProfitLossDateRangePicker";

// Packages
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Tooltip from "../../../Shared/Tooltip";

// Components
import Chip from "../../../Shared/ui/Chip";

// Assets
import { CalendarPlus, CornerDownRight, Plus, Minus, X } from "lucide-react";

const ProfitLossReport = forwardRef(({ projection, allMeasurements, allPriceBundles, ...props }, ref) => {
  const classes = useStyles();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  const generalLedgerRef = useRef(null);
  // Account Detail
  const [accountDetailMeasurementRows, setAccountDetailMeasurementRows] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedAccountLinkedDetails, setSelectedAccountLinkedDetails] = useState([]);

  const [currency, setCurrency] = useState({ symbol: "€" });

  // compareDates
  const [compareDates, setCompareDates] = useState(null);

  // Projection
  const [activeProjection, setActiveProjection] = useState(null);
  const [projectionData, setProjectionData] = useState([]);

  useEffect(() => {
    if (props.profitLossDates) {
      let tempCompareDates = props.profitLossDates;
      // order by sequence
      if (tempCompareDates.length > 0) {
        tempCompareDates.sort((a, b) => a.sequence - b.sequence);
      }
      setCompareDates(tempCompareDates);
    }
  }, [props.profitLossDates]);

  // Calculate projection data
  useEffect(() => {
    if (projection !== activeProjection || props.profitLossReportData !== projectionData) {
      setActiveProjection(projection);
      // calculate projection data (starting from profitLossReportData)
      let tempProjectionData = JSON.parse(JSON.stringify(props.profitLossReportData));

      const hasPriceDefinitions = projection?.priceDefinitions?.length > 0;
      const hasAccountProjections = projection?.accounts?.length > 0;

      if (tempProjectionData && hasPriceDefinitions && allPriceBundles) {
        // Price definition projection: compute per-account ratio from raw measurements
        const accountRatios = {};
        const addRatio = (accountId, original, projected) => {
          if (!accountId) return;
          if (!accountRatios[accountId]) accountRatios[accountId] = { original: 0, projected: 0 };
          accountRatios[accountId].original += original;
          accountRatios[accountId].projected += projected;
        };
        if (allMeasurements && allMeasurements.length > 0) {
          allMeasurements.forEach((m) => {
            const eventPrice = m.eventPrice || 0;
            if (m.priceBundleId) {
              let bundle = null;
              for (const company of allPriceBundles) {
                const found = company.priceBundles.find((b) => b.id === m.priceBundleId);
                if (found) { bundle = found; break; }
              }
              if (bundle && bundle.items.length > 0) {
                const originalBundleTotal = bundle.items.reduce((sum, item) => sum + item.price * item.multiplier, 0);
                bundle.items.forEach((item) => {
                  const pdProj = projection.priceDefinitions.find((pd) => pd.priceDefinitionId === item.priceDefinitionId);
                  const itemNewPrice = pdProj ? pdProj.newPrice : item.price;
                  const itemOriginalFrac = originalBundleTotal > 0 ? (item.price * item.multiplier) / originalBundleTotal : 0;
                  const itemProjectedFrac = originalBundleTotal > 0 ? (itemNewPrice * item.multiplier) / originalBundleTotal : 0;
                  addRatio(item.effectiveAccountId, eventPrice * itemOriginalFrac, eventPrice * itemProjectedFrac);
                });
              } else {
                addRatio(m.accountId, eventPrice, eventPrice);
              }
            } else {
              addRatio(m.accountId, eventPrice, eventPrice);
            }
          });
        }

        const applyPdProjectionToCategories = (categories) => {
          categories.forEach((category) => {
            let categoryTotal = 0;
            category.accountResults.forEach((account) => {
              let totals = Object.values(account.total);
              let lastTotal = totals[totals.length - 1];
              const ratio = accountRatios[account.id];
              if (ratio && ratio.original > 0) {
                account.total = lastTotal * (ratio.projected / ratio.original);
                account.percentage = lastTotal !== 0 ? ((account.total - lastTotal) / Math.abs(lastTotal)) * 100 : 0;
              } else {
                account.total = lastTotal;
                account.percentage = 0;
              }
              categoryTotal += account.total;
            });
            category.total = categoryTotal;
          });
        };

        applyPdProjectionToCategories(tempProjectionData.revenueAccountCategoryResults);
        applyPdProjectionToCategories(tempProjectionData.costAccountCategoryResults);

        tempProjectionData.revenueTotal = tempProjectionData.revenueAccountCategoryResults.reduce((acc, cat) => acc + cat.total, 0);
        tempProjectionData.costTotal = tempProjectionData.costAccountCategoryResults.reduce((acc, cat) => acc + cat.total, 0);
        tempProjectionData.totalProfit = tempProjectionData.revenueTotal - tempProjectionData.costTotal;

        setProjectionData(tempProjectionData);
      } else if (tempProjectionData && hasAccountProjections) {
        tempProjectionData.revenueAccountCategoryResults.forEach((category) => {
          let categoryTotal = 0;
          category.accountResults.forEach((account) => {
            // if there is an account projection in projection.accounts (projection.accounts[account.id]), multiply the account total by projection.accounts[account.id].percentage / 100
            let accountProjection = projection.accounts.find((accountProjection) => accountProjection.id === account.id);
            if (accountProjection) {
              let totals = Object.values(account.total);
              account.total = totals[totals.length - 1] * (parseFloat(accountProjection.percentage) / 100);
              // percentage is what you have to do to og price to get the new price so for example og price is 100 and new price is 80 then percentage is -20% so percentage is the difference between the og price and the new price divided by the og price times 100 so in this case it would be (100 - 80) / 100 * 100 = 20%
              account.percentage = totals[totals.length - 1] !== 0 ? ((account.total - totals[totals.length - 1]) / Math.abs(totals[totals.length - 1])) * 100 : 0;
            } else {
              account.total = Object.values(account.total)[Object.values(account.total).length - 1];
              account.percentage = 0;
            }
            categoryTotal += account.total;
          });
          category.total = categoryTotal;
        });
        tempProjectionData.costAccountCategoryResults.forEach((category) => {
          let categoryTotal = 0;
          category.accountResults.forEach((account) => {
            // if there is an account projection in projection.accounts (projection.accounts[account.id]), multiply the account total by projection.accounts[account.id].percentage / 100
            let accountProjection = projection.accounts.find((accountProjection) => accountProjection.id === account.id);
            if (accountProjection) {
              let totals = Object.values(account.total);
              account.total = totals[totals.length - 1] * (parseFloat(accountProjection.percentage) / 100);
              account.percentage = totals[totals.length - 1] !== 0 ? ((account.total - totals[totals.length - 1]) / Math.abs(totals[totals.length - 1])) * 100 : 0;
            } else {
              account.total = Object.values(account.total)[Object.values(account.total).length - 1];
              account.percentage = 0;
            }
            categoryTotal += account.total;
          });

          category.total = categoryTotal;
        });

        tempProjectionData.revenueTotal = tempProjectionData.revenueAccountCategoryResults.reduce((acc, category) => {
          return acc + category.total;
        }, 0);

        tempProjectionData.costTotal = tempProjectionData.costAccountCategoryResults.reduce((acc, category) => {
          return acc + category.total;
        }, 0);

        tempProjectionData.totalProfit = tempProjectionData.revenueTotal - tempProjectionData.costTotal;

        setProjectionData(tempProjectionData);
      }
    }
  }, [projection, props.profitLossReportData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.currency) {
      setCurrency(props.currency);
    }
  }, [props.currency]);

  useEffect(() => {
    if (props.profitLossReportData) {
      if (JSON.parse(JSON.stringify(props.profitLossReportData)) !== JSON.parse(JSON.stringify(reportData))) {
        setReportData(props.profitLossReportData);
        setLoading(false);
      }
    }
  }, [props.profitLossReportData, props.emptyState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to generate custom filename
  const generateFileName = () => {
    const formattedDate = getFormattedDate();
    const chartTitle = "ProfitLossReport"; // You can customize this or make it dynamic
    if (props.graphConfigurationName !== "" && props.graphConfigurationName !== undefined && props.graphConfigurationName !== null) {
      return `${chartTitle}_${props.graphConfigurationName}_${formattedDate}`;
    } else {
      return `${chartTitle}_${formattedDate}`;
    }
  };

  const handleDownloadReport = async () => {
    const element = generalLedgerRef.current;

    if (!element) {
      console.error("Element not found");
      return;
    }

    try {
      const canvas = await html2canvas(element, { scale: 2 }); // Higher scale for better quality
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4"); // Portrait, millimeters, A4 size
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = Math.min(canvas.width / 4, pageWidth); // Scale to fit A4 width
      const imgHeight = (imgWidth / canvas.width) * canvas.height;

      let y = 10; // Start position on the PDF
      if (imgHeight > pageHeight - 10) {
        pdf.addImage(imgData, "PNG", 10, y, imgWidth, pageHeight - 20);
        pdf.addPage();
      } else {
        pdf.addImage(imgData, "PNG", 10, y, imgWidth, imgHeight);
      }

      const fileName = "generalLedgerAccount.pdf";
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  // Expose the download report function to the parent component
  useImperativeHandle(ref, () => ({
    downloadReport: handleDownloadReport,
  }));

  const formatPrice = (price) => {
    if (price === null || price === undefined || price === "") {
      return "0,00";
    } else {
      return price
        .toFixed(2) // Ensure two decimal places
        .replace(".", ",") // Replace decimal point with comma
        .replace(/\B(?=(\d{3})+(?!\d))/g, " "); // Add space as thousands separator
    }
  };

  const getFormattedDate = () => {
    const date = new Date();
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    // Get date in MM-DD-YYYY format
    return date.toLocaleDateString(undefined, options).replace(/\//g, "-");
  };

  const getEmptyStateTitle = () => {
    if (reportData === null) {
      return "No data available";
    }
  };
  const getEmptyStateSubtitle = () => {
    if (reportData === null) {
      return "No data available for the selected date range";
    }
  };

  const showAccountDetail = (accountId) => {
    setSelectedAccountId(accountId);

    let accountLinkedDetails = [];
    let tempAccountDetailMeasurementRows = props.data.filter((measurement) => measurement.accountId === accountId);

    if (tempAccountDetailMeasurementRows.length > 0) {
      tempAccountDetailMeasurementRows.forEach((measurement) => {
        let detail = accountLinkedDetails.find((detail) => detail.name === measurement.description);
        if (detail) {
          detail.totalPrice += measurement.eventPrice;
        } else {
          accountLinkedDetails.push({ name: measurement.description, totalPrice: measurement.eventPrice });
        }
      });
    }

    // order by name
    accountLinkedDetails.sort((a, b) => a.name.localeCompare(b.name));

    setSelectedAccountLinkedDetails(accountLinkedDetails);
    setAccountDetailMeasurementRows(tempAccountDetailMeasurementRows);
  };

  const getSelectedAccountName = () => {
    if (selectedAccountId) {
      let costAccount = reportData.costAccountCategoryResults
        ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
        .find((result) => result.id === selectedAccountId); // Find the desired account

      let revenueAccount = reportData.revenueAccountCategoryResults
        ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
        .find((result) => result.id === selectedAccountId); // Find the desired account

      return costAccount?.name || revenueAccount?.name;
    } else {
      return "All";
    }
  };

  const isSelectedAccountRevenue = () => {
    if (selectedAccountId) {
      let revenueAccount = reportData.revenueAccountCategoryResults
        ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
        .find((result) => result.id === selectedAccountId); // Find the desired account

      return revenueAccount !== undefined;
    } else {
      return false;
    }
  };
  const getAccountDetailPercentage = (price) => {
    // Get the total account price
    let totalAccountPrice = getSelectedAccountTotalPrice();

    // Ensure the totalAccountPrice is not zero to avoid division by zero
    if (totalAccountPrice === 0) {
      throw new Error("Total account price is zero, cannot calculate percentage.");
    }

    // Calculate the percentage
    let percentage = (price / totalAccountPrice) * 100;

    // Round to 2 decimal places and ensure absolute value
    percentage = Math.abs(Math.round(percentage * 100) / 100);

    return percentage;
  };
  const limitString = (str, limit) => {
    if (str.length > limit) {
      return str.substring(0, limit) + "...";
    } else {
      return str;
    }
  };

  // Compute price-definition breakdown for an account in a given compare period.
  // Distributes each measurement's signed eventPrice across its bundle items by share
  // (item.price * item.multiplier / bundleTotal), keyed by priceDefinitionName.
  // Measurements without a (valid) bundle go into "Uncategorized".
  const getAccountPriceDefinitionBreakdown = (account, totalIndex) => {
    if (!account || !allMeasurements || allMeasurements.length === 0 || !compareDates) {
      return [];
    }

    const dateEntries = Object.values(compareDates);
    const range = dateEntries[totalIndex];
    if (!range) return [];

    const from = new Date(range.fromDate);
    const to = new Date(range.toDate ?? range.endDate);

    // Bundle lookup
    const bundleById = {};
    if (allPriceBundles) {
      for (const company of allPriceBundles) {
        if (!company?.priceBundles) continue;
        for (const b of company.priceBundles) {
          bundleById[b.id] = b;
        }
      }
    }

    const totals = {}; // name -> total
    const UNCATEGORIZED = "Uncategorized";

    allMeasurements.forEach((m) => {
      if (m.accountId !== account.id) return;
      const inserted = new Date(m.insertedOn);
      if (!(inserted > from && inserted <= to)) return;

      const signedPrice = (m.isNegative ? -1 : 1) * (m.eventPrice || 0);
      if (signedPrice === 0) return;

      const bundle = m.priceBundleId ? bundleById[m.priceBundleId] : null;
      if (bundle && bundle.items && bundle.items.length > 0) {
        const bundleTotal = bundle.items.reduce((s, it) => s + it.price * it.multiplier, 0);
        if (bundleTotal > 0) {
          bundle.items.forEach((it) => {
            const share = (it.price * it.multiplier) / bundleTotal;
            const name = it.priceDefinitionName || UNCATEGORIZED;
            totals[name] = (totals[name] || 0) + signedPrice * share;
          });
          return;
        }
      }
      totals[UNCATEGORIZED] = (totals[UNCATEGORIZED] || 0) + signedPrice;
    });

    return Object.entries(totals)
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const categoryTooltip = (category, price, isCost = false, totalIndex = 0, isProjection = false) => {
    // Find the total revenue or cost (sum of all categories) for the correct comparison period
    let total = 0;
    let label = "of total Revenue";
    if (isProjection) {
      // Use projectionData for totals
      if (isCost) {
        total = projectionData?.costTotal ?? 0;
        label = "of simulated COGS";
      } else {
        total = projectionData?.revenueTotal ?? 0;
        label = "of simulated Revenue";
      }
    } else {
      if (isCost) {
        if (reportData && reportData.costTotal) {
          if (Array.isArray(reportData.costTotal)) {
            total = reportData.costTotal[totalIndex] ?? 0;
          } else if (typeof reportData.costTotal === "object") {
            total = Object.values(reportData.costTotal)[totalIndex] ?? 0;
          }
        }
        label = "of total COGS";
      } else {
        if (reportData && reportData.revenueTotal) {
          if (Array.isArray(reportData.revenueTotal)) {
            total = reportData.revenueTotal[totalIndex] ?? 0;
          } else if (typeof reportData.revenueTotal === "object") {
            total = Object.values(reportData.revenueTotal)[totalIndex] ?? 0;
          }
        }
      }
    }

    // Calculate percentage for the correct period
    const percentage = total ? ((price / total) * 100).toFixed(2) : 0;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", paddingRight: "2px" }}>
          <p style={{ fontSize: "18px" }}>{category.name}</p>
          <Chip variant={isCost ? "danger" : "success"} size="extraSmall">
            {currency.symbol}
            {formatPrice(price)}
          </Chip>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          <div style={{ width: "2px", borderRadius: "2px", height: "19px", backgroundColor: isCost ? "#D84949" : "#3AB037", display: "inline-block" }} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
            <p style={{ fontSize: "16px" }}>
              {percentage}% {label}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const customTooltip = (account, price, isCost = false, totalIndex = 0, isProjection = false, category = null) => {
    // Find the total revenue or cost (sum of all accounts) for the correct comparison period
    let total = 0;
    let label = "of total Revenue";
    if (isProjection) {
      // Use projectionData for totals
      if (isCost) {
        total = projectionData?.costTotal ?? 0;
        label = "of simulated COGS";
      } else {
        total = projectionData?.revenueTotal ?? 0;
        label = "of simulated Revenue";
      }
    } else {
      if (isCost) {
        if (reportData && reportData.costTotal) {
          if (Array.isArray(reportData.costTotal)) {
            total = reportData.costTotal[totalIndex] ?? 0;
          } else if (typeof reportData.costTotal === "object") {
            total = Object.values(reportData.costTotal)[totalIndex] ?? 0;
          }
        }
        label = "of total COGS";
      } else {
        if (reportData && reportData.revenueTotal) {
          if (Array.isArray(reportData.revenueTotal)) {
            total = reportData.revenueTotal[totalIndex] ?? 0;
          } else if (typeof reportData.revenueTotal === "object") {
            total = Object.values(reportData.revenueTotal)[totalIndex] ?? 0;
          }
        }
      }
    }

    // Calculate percentage for the correct period
    const percentage = total ? ((price / total) * 100).toFixed(2) : 0;

    // Calculate percentage of parent category
    let categoryTotal = 0;
    let categoryPercentage = 0;
    let categoryLabel = "";

    if (category) {
      if (isProjection) {
        // Use projectionData for category totals
        if (isCost) {
          const projectionCategory = projectionData.costAccountCategoryResults.find((item) => item.id === category.id);
          categoryTotal = projectionCategory?.total ?? 0;
        } else {
          const projectionCategory = projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id);
          categoryTotal = projectionCategory?.total ?? 0;
        }
      } else {
        // Use reportData for category totals
        if (Array.isArray(category.total)) {
          categoryTotal = category.total[totalIndex] ?? 0;
        } else if (typeof category.total === "object") {
          categoryTotal = Object.values(category.total)[totalIndex] ?? 0;
        } else {
          categoryTotal = category.total ?? 0;
        }
      }

      categoryPercentage = categoryTotal ? ((price / categoryTotal) * 100).toFixed(2) : 0;
      categoryLabel = `of ${category.name}`;
    }

    const breakdown = !isProjection ? getAccountPriceDefinitionBreakdown(account, totalIndex) : [];

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", paddingRight: "2px" }}>
          <p style={{ fontSize: "18px" }}>{account.name}</p>
          <Chip variant={isCost ? "danger" : "success"} size="extraSmall">
            {currency.symbol}
            {formatPrice(price)}
          </Chip>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
          <div style={{ display: "flex", gap: "4px" }}>
            <div style={{ width: "2px", borderRadius: "2px", height: "19px", backgroundColor: isCost ? "#D84949" : "#3AB037", display: "inline-block" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
              <p style={{ fontSize: "16px" }}>
                {percentage}% {label}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: "4px" }}>
            <div style={{ width: "2px", borderRadius: "2px", height: "19px", backgroundColor: isCost ? "#D84949" : "#3AB037", display: "inline-block" }} />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
              <p style={{ fontSize: "16px" }}>
                {categoryPercentage}% {categoryLabel}
              </p>
            </div>
          </div>
        </div>
        {breakdown.length > 0 && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "4px", borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: "6px", marginTop: "2px" }}>
            <p style={{ fontSize: "13px", opacity: 0.7, margin: 0 }}>Breakdown</p>
            {breakdown.map((row) => (
              <div key={row.name} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", fontSize: "14px" }}>
                <span style={{ opacity: 0.85 }}>{row.name}</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {currency.symbol}
                  {formatPrice(row.total)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const getSelectedAccountTotalPrice = () => {
    if (selectedAccountId) {
      let costAccount = reportData.costAccountCategoryResults
        ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
        .find((result) => result.id === selectedAccountId); // Find the desired account

      let revenueAccount = reportData.revenueAccountCategoryResults
        ?.flatMap((category) => category.accountResults) // Flatten all accountResults arrays into one
        .find((result) => result.id === selectedAccountId); // Find the desired account

      return revenueAccount?.total ? `+${revenueAccount.total.toFixed(2)}` : (costAccount?.total || 0).toFixed(2);
    } else {
      return reportData.totalProfit;
    }
  };
  const setDateRange = (index, startDate, endDate, sequence) => {
    props.setCompareDateRange(index, startDate, endDate, sequence);
  };
  const resetDateRangeFilter = () => {};
  const handleSetStartDate = (event) => {};
  const handleSetEndDate = (event) => {};

  const handleAddComparePeriod = () => {
    props.comparePeriod();
  };

  return (
    <div className={classes.reportContainer}>
      {reportData.length == 0 ? (
        <div className={classes.emptyStateContainer}>
          <div className={classes.emptyStateCard}>
            <p className={classes.emptyStateCard__title}>{getEmptyStateTitle()}</p>
            <p className={classes.emptyStateCard__subtitle}>{getEmptyStateSubtitle()}</p>
          </div>
        </div>
      ) : (
        <div ref={generalLedgerRef}>
          <div className={classes.reportContainer__report}>
            <div className={classes.report__buttonContainer}>
              <button className={classes.addCalenderColumn} onClick={() => props.comparePeriod()} disabled={props.compareLimit <= Object.entries(compareDates).length}>
                <CalendarPlus className={classes.addCalenderColumn__icon} />
              </button>
              <button className={classes.removeCompareDates} onClick={() => props.resetComparePeriod()} disabled={Object.entries(compareDates).length < 2}>
                <X className={classes.removeCompareDates__icon} />
              </button>
            </div>
            <div className={classes.report__header}>
              <h2 className={classes.report__title}>P&L statement for {reportData.companies.join(", ")}</h2>
            </div>
            <div class={classes.revenueCostBlock}>
              <table class="no-stripes" style={{ width: "100% !important", borderSpacing: "0px " }}>
                <thead className={classes.highLow__headerContainer}>
                  <th>
                    <span className={classes.highLow__detailHeader}>
                      <p className={classes.highLow__detailTitle}>{"Revenue"}</p>
                      <Plus style={{ color: "rgb(40, 167, 69)", paddingRight: "10px" }} />
                    </span>
                  </th>
                  {Object.entries(compareDates).map(([key, date], totalIndex) => (
                    <th style={{ width: "250px", textAlign: "right" }} align="right">
                      <ProfitLossDateRangePicker
                        size="small"
                        index={totalIndex}
                        setDateRange={setDateRange}
                        resetDateRangeFilter={resetDateRangeFilter}
                        sequence={date.sequence}
                        startDate={date.fromDate}
                        endDate={date.endDate}
                        handleSetStartDate={handleSetStartDate}
                        handleSetEndDate={handleSetEndDate}
                      ></ProfitLossDateRangePicker>
                    </th>
                  ))}
                  {activeProjection && (
                    <th style={{ width: "250px", textAlign: "right" }} align="right">
                      Simulation
                    </th>
                  )}
                </thead>
                <tbody className={classes.profitLossBalance}>
                  {reportData.revenueAccountCategoryResults.map((category, index) => (
                    <>
                      <tr className={classes.accountCategory} key={category.accountCategoryId}>
                        <td className={classes.accountCategoryCell}>
                          <h4 className={classes.accountCategory__title}>
                            <span className={classes.accountCategory__index}>{index + 1}.</span>
                            {category.name}:
                          </h4>
                        </td>{" "}
                        {Object.entries(category.total).map(([key, total], totalIndex) => (
                          <td align="right" className={classes.accountCategoryCell}>
                            <Tooltip content={categoryTooltip(category, total, false, totalIndex)}>
                              <p className={classes.account__price} key={`categorytotalIndex_${totalIndex}`}>
                                {currency.symbol}
                                {formatPrice(total)}
                              </p>
                            </Tooltip>
                          </td>
                        ))}{" "}
                        {activeProjection && (
                          <td align="right" className={classes.accountCategoryCell}>
                            <Tooltip content={categoryTooltip(category, projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id)?.total, false, 0, true)}>
                              <p className={classes.account__price} key={`categorytotalIndex_projection`}>
                                {currency.symbol}
                                {formatPrice(projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id)?.total)}
                              </p>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                      {category.accountResults.map((account) => (
                        <tr className={`${classes.account} ${selectedAccountId === account.id ? classes.account__highlighted : ""}`}>
                          <td className={classes.accountCategoryCell}>
                            <span className={classes.subdivision}>
                              <CornerDownRight className={`${classes.cornerDownRight} ${selectedAccountId === account.id ? classes.cornerWhiteDownRight : ""}`}></CornerDownRight>
                              <p className={classes.account__title}>{account.name}:</p>
                            </span>
                          </td>
                          {Object.entries(account.total).map(([key, total], totalIndex) => (
                            <React.Fragment key={`totalIndex_fragment_${totalIndex}`}>
                              <td align="right" className={classes.accountCategoryCell}>
                                <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                  {props.percentageVisible &&
                                    (() => {
                                      const totals = Object.values(account.total);
                                      const previous = totals[totalIndex - 1];
                                      if (previous === undefined) return null;
                                      const difference = total - previous;

                                      const color = difference !== 0 ? (difference < 0 ? "danger" : "success") : "";
                                      const formattedDifference =
                                        difference !== 0
                                          ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                          : "=";

                                      return (
                                        <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                          {formattedDifference}
                                        </Chip>
                                      );
                                    })()}
                                  <Tooltip content={customTooltip(account, total, false, totalIndex, false, category)}>
                                    <p className={classes.account__price_sm} key={`totalIndex_${totalIndex}`}>
                                      {currency.symbol}
                                      {formatPrice(total)}
                                    </p>
                                  </Tooltip>
                                </span>
                              </td>
                            </React.Fragment>
                          ))}
                          {activeProjection && (
                            <td align="right" className={classes.accountCategoryCell}>
                              <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                {props.percentageVisible &&
                                  (() => {
                                    const projectedTotal = projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total;
                                    const historicalTotals = Object.values(account.total);
                                    const lastHistorical = historicalTotals[historicalTotals.length - 1];
                                    if (projectedTotal === undefined || lastHistorical === undefined) return null;
                                    const difference = projectedTotal - lastHistorical;

                                    const color = difference !== 0 ? (difference < 0 ? "danger" : "success") : "";
                                    const formattedDifference =
                                      difference !== 0
                                        ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                        : "=";

                                    return (
                                      <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                        {formattedDifference}
                                      </Chip>
                                    );
                                  })()}{" "}
                                <Tooltip
                                  content={customTooltip(
                                    account,
                                    projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total,
                                    false,
                                    0,
                                    true,
                                    category
                                  )}
                                >
                                  <p className={classes.account__price_sm} key={`totalIndex_projection`}>
                                    {currency.symbol}
                                    {formatPrice(projectionData.revenueAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total)}
                                  </p>
                                </Tooltip>
                              </span>
                            </td>
                          )}
                        </tr>
                      ))}
                    </>
                  ))}
                  <tr className={classes.accountCategoryTotal}>
                    <td className={classes.accountCategoryCell}>
                      <p className={classes.total__title}>TOTAL REVENUES:</p>
                    </td>
                    {Object.entries(reportData.revenueTotal).map(([key, total], totalIndex) => (
                      <td align="right" className={classes.accountCategoryCell}>
                        {currency.symbol}
                        {formatPrice(total)}
                      </td>
                    ))}
                    {activeProjection && (
                      <td align="right" className={classes.accountCategoryCell}>
                        {currency.symbol}
                        {formatPrice(projectionData.revenueTotal)}
                      </td>
                    )}
                  </tr>
                </tbody>
                <thead className={classes.highLow__headerContainer}>
                  <th>
                    <span className={classes.highLow__detailHeader}>
                      <p className={classes.highLow__detailTitle}>{"Cost of Goods Sold (COGS)"}</p>
                      <Minus style={{ color: "rgb(220, 53, 69)", paddingRight: "10px" }} />
                    </span>
                  </th>
                  {Object.entries(compareDates).map(([key, date], totalIndex) => (
                    <th align="right">
                      {/* <span className={classes.report__subtitle}><span>{parseNewDateToDDMMYYYWithoutSeconds(date.fromDate)} </span> <span>{parseNewDateToDDMMYYYWithoutSeconds(date.toDate)}</span></span> */}
                    </th>
                  ))}
                  {activeProjection && <th align="right"></th>}
                </thead>
                <tbody className={classes.profitLossBalance}>
                  {[...reportData.costAccountCategoryResults]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((category, index) => (
                      <>
                        <tr className={classes.accountCategory} key={category.accountCategoryId}>
                          <td className={classes.accountCategoryCell}>
                            <h4 className={classes.accountCategory__title}>
                              <span className={classes.accountCategory__index}>{index + 1}.</span>
                              {category.name}:
                            </h4>
                          </td>{" "}
                          {Object.entries(category.total).map(([key, total], totalIndex) => (
                            <td align="right" className={classes.accountCategoryCell} key={`categorytotalIndex_${totalIndex}`}>
                              <Tooltip content={categoryTooltip(category, total, true, totalIndex)}>
                                <p className={classes.account__price}>
                                  {currency.symbol}
                                  {formatPrice(total)}
                                </p>
                              </Tooltip>
                            </td>
                          ))}{" "}
                          {activeProjection && (
                            <td align="right" className={classes.accountCategoryCell}>
                              <Tooltip content={categoryTooltip(category, projectionData.costAccountCategoryResults.find((item) => item.id === category.id)?.total, true, 0, true)}>
                                <p className={classes.account__price} key={`categorytotalIndex_projection`}>
                                  {currency.symbol}
                                  {/* show price of costAccountCategoryResutls[?] where .id === category.id */}
                                  {formatPrice(projectionData.costAccountCategoryResults.find((item) => item.id === category.id)?.total)}
                                </p>
                              </Tooltip>
                            </td>
                          )}
                        </tr>
                        {[...category.accountResults]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((account) => (
                            <tr className={`${classes.account} ${selectedAccountId === account.id ? classes.account__highlighted : ""}`} key={account.id}>
                              <td className={classes.accountCategoryCell}>
                                <span className={classes.subdivision}>
                                  <CornerDownRight className={`${classes.cornerDownRight} ${selectedAccountId === account.id ? classes.cornerWhiteDownRight : ""}`} />
                                  <p className={classes.account__title}>{account.name}:</p>
                                </span>
                              </td>
                              {Object.entries(account.total).map(([key, total], totalIndex) => (
                                <React.Fragment key={`totalIndex_fragment_${totalIndex}`}>
                                  <td align="right" className={classes.accountCategoryCell} key={`totalIndex_${totalIndex}`}>
                                    <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                      {props.percentageVisible &&
                                        (() => {
                                          const totals = Object.values(account.total);
                                          const previous = totals[totalIndex - 1];
                                          if (previous === undefined) return null;
                                          const difference = total - previous;

                                          const color = difference !== 0 ? (difference < 0 ? "success" : "danger") : "";
                                          const formattedDifference =
                                            difference !== 0
                                              ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                              : "=";

                                          return (
                                            <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                              {formattedDifference}
                                            </Chip>
                                          );
                                        })()}
                                      <Tooltip content={customTooltip(account, total, true, totalIndex, false, category)}>
                                        <p className={classes.account__price_sm}>
                                          {currency.symbol}
                                          {formatPrice(total)}
                                        </p>
                                      </Tooltip>
                                    </span>
                                  </td>
                                </React.Fragment>
                              ))}
                              {activeProjection && (
                                <td align="right" className={classes.accountCategoryCell}>
                                  <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                                    {props.percentageVisible &&
                                      (() => {
                                        const projectedTotal = projectionData.costAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total;
                                        const historicalTotals = Object.values(account.total);
                                        const lastHistorical = historicalTotals[historicalTotals.length - 1];
                                        if (projectedTotal === undefined || lastHistorical === undefined) return null;
                                        const difference = projectedTotal - lastHistorical;

                                        const color = difference !== 0 ? (difference < 0 ? "success" : "danger") : "";
                                        const formattedDifference =
                                          difference !== 0
                                            ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                            : "=";

                                        return (
                                          <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                            {formattedDifference}
                                          </Chip>
                                        );
                                      })()}{" "}
                                    <Tooltip
                                      content={customTooltip(
                                        account,
                                        projectionData.costAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total,
                                        true,
                                        0,
                                        true,
                                        category
                                      )}
                                    >
                                      <p className={classes.account__price_sm} key={`totalIndex_projection`}>
                                        {currency.symbol}
                                        {formatPrice(projectionData.costAccountCategoryResults.find((item) => item.id === category.id)?.accountResults.find((item) => item.id === account.id)?.total)}
                                      </p>
                                    </Tooltip>
                                  </span>
                                </td>
                              )}
                            </tr>
                          ))}
                      </>
                    ))}
                  <tr className={classes.accountCategoryTotal}>
                    <td className={classes.accountCategoryCell}>
                      <p className={classes.total__title}>Total COGS:</p>
                    </td>
                    {Object.entries(reportData.costTotal).map(([key, total], totalIndex) => (
                      <td align="right" className={classes.accountCategoryCell} key={`costTotal_${totalIndex}`}>
                        {currency.symbol}
                        {formatPrice(total)}
                      </td>
                    ))}
                    {activeProjection && (
                      <td align="right" className={classes.accountCategoryCell}>
                        {currency.symbol}
                        {formatPrice(projectionData.costTotal)}
                      </td>
                    )}
                  </tr>
                  <tr className={classes.report__total}>
                    <td className={classes.accountCategoryCell}>
                      <span className={classes.total__title}>Gross Profit:</span>
                    </td>
                    {Object.entries(reportData.totalProfit).map(([key, total], totalIndex) => (
                      <td align="right" className={classes.accountCategoryCell} key={`totalGrossProfit${totalIndex}`}>
                        <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                          {props.percentageVisible &&
                            totalIndex > 0 &&
                            (() => {
                              const values = Object.values(reportData.totalProfit);
                              const first = values[0];
                              const last = values[values.length - 1];
                              const difference = last - first;

                              const color = difference !== 0 ? (difference < 0 ? "danger" : "success") : "";
                              const formattedDifference =
                                difference !== 0
                                  ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                  : "=";

                              return (
                                <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                  {formattedDifference}
                                </Chip>
                              );
                            })()}
                          <span className={classes.total__title}>
                            {currency.symbol}
                            {formatPrice(total)}
                          </span>
                        </span>
                      </td>
                    ))}
                    {activeProjection && (
                      <td align="right" className={classes.accountCategoryCell}>
                        <span style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: "8px" }}>
                          {props.percentageVisible &&
                            (() => {
                              const lastProfit = Object.values(reportData.totalProfit).slice(-1)[0];
                              const difference = projectionData.totalProfit - lastProfit;

                              const color = difference !== 0 ? (difference > 0 ? "success" : "danger") : "";
                              const formattedDifference =
                                difference !== 0
                                  ? `${difference > 0 ? "+" : "-"}${currency.symbol}${formatPrice(Math.abs(difference))}`
                                  : "=";

                              return (
                                <Chip variant={color} size="extraSmall" style={{ whiteSpace: "nowrap" }}>
                                  {formattedDifference}
                                </Chip>
                              );
                            })()}
                          <span className={classes.total__title}>
                            {currency.symbol}
                            {formatPrice(projectionData.totalProfit)}
                          </span>
                        </span>
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ProfitLossReport;

const useStyles = makeStyles(() => ({
  reportContainer: {
    // if content is too wide, hide behind horizontal scroll
    overflowX: "auto",
  },

  reportContainer__report: {
    width: "fit-content",
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    flexDirection: "column",

    backgroundColor: "white",

    borderRadius: "8px",
    border: "1px solid #EDEDED",

    padding: "25px",
    ":hover": {
      backgroundColor: "red",
    },
  },
  highLow__headerContainer: {
    width: "100%",

    alignItems: "center",

    backgroundColor: "#ede6df",
  },

  highLow__detailHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",

    fontSize: "16px",
    fontWeight: "600",

    padding: "10px",
  },
  accountDetail__disclaimer: {
    width: "100%",

    backgroundColor: "#F4F4F4",
    borderRadius: "8px",

    padding: "8px",
  },
  reportContainer__accountDetail: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: "8px",
    border: "none",
    transition: "width 0.2s ease-in-out, opacity 0.2s ease-in-out, padding 0.2s ease-in-out",
    width: "0px", // Initial state: hidden
    opacity: 0,
    padding: "16px 0",
  },

  active: {
    width: "560px", // Expand to fit content
    opacity: 1, // Fully visible
    padding: "16px", // Restore padding
    border: "1px solid #EDEDED",
  },

  accountDetail__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },
  accountDetail__header: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "16px",
  },

  accountDetail__content: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "16px",
  },
  accountDetail__details: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  accountDetail__detail: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",

    whiteSpace: "nowrap",
    overflow: "hidden",
  },

  accountDetail__disclaimerText: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0px",

    color: "#0C0E29",
  },
  accountDetail__total: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",

    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
    margin: "0px",

    color: "#0C0E29",

    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    borderTop: "1px solid #EDEDED",
    paddingTop: "8px",
  },
  highLow__detailTitle: {
    margin: "0",
  },
  reportCard: {
    border: "1px solid #EDEDED",
    ":hover": {
      backgroundColor: "red",
    },
  },

  report__buttonContainer: {
    height: "fit-content",
    width: "fit-content",

    display: "flex",
    flexDirection: "column",
    gap: "8px",
    alignItems: "center",

    position: "absolute",
    right: "-12px",
    top: "50%",
    transform: "translateY(-24px)",
  },

  addCalenderColumn: {
    height: "48px",
    width: "24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",

    padding: "0px",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#F4F4F4",
      cursor: "pointer",
    },

    "&:disabled": {
      backgroundColor: "white",
      cursor: "not-allowed",
    },
  },

  addCalenderColumn__icon: {
    height: "16px",
    width: "16px",

    strokeWidth: "1.4px",

    color: "#0C0E29",
  },

  removeCompareDates: {
    height: "24px",
    width: "24px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    backgroundColor: "white",
    border: "1px solid #EDEDED",
    borderRadius: "8px",
    color: "#D84949",

    padding: "0px",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "white",
      cursor: "pointer",
    },

    "&:disabled": {
      backgroundColor: "white",
      color: "#D84949",
      cursor: "not-allowed",

      transform: "translateY(-32px)",
      zIndex: "-10",
    },
  },

  removeCompareDates__icon: {
    height: "16px",
    width: "16px",

    strokeWidth: "1.4px",

    color: "inherit",
  },

  report__header: {
    width: "fit-content",
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    gap: "8px",

    marginBottom: "16px",
  },

  report__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "24px",
    margin: "0px",

    color: "#0C0E29",
  },

  report__timeRangeContainer: {
    width: "fit-content",

    position: "relative",

    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  report__timeRangeIndicatorContainer: {
    width: "100%",
    height: "8px",

    backgroundColor: "#EDEDED",

    borderRadius: "4px",
  },

  report__timeRangeIndicator: {
    height: "8px",

    backgroundColor: "#5CC958",

    borderRadius: "4px",

    ":hover": {
      backgroundColor: "red",
      top: "12px",
    },
  },

  report__subtitle: {
    width: "fit-content",

    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    gap: "4px",

    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0px",

    color: "#0C0E29",
    opacity: "0.6",
  },

  report__subdivision: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",

    marginBottom: "8px",
  },
  accountCategoryTotal: {
    width: "100%",
    borderTop: "1px solid grey",
    backgroundColor: "transparent !important",
  },

  accountCategory: {
    width: "100%",
    backgroundColor: "transparent !important",
  },
  accountCategoryCell: {
    border: "0px !important",
    color: "inherit",
    paddingRight: "4px",
  },

  accountCategory__header: {
    width: "100%",
    boxSizing: "border-box",
    padding: "0px 4px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },

  accountCategory__index: {
    width: "16px",

    display: "inline-block",

    fontFamily: "Lato, sans-serif",
    fontSize: "18px",
    fontWeight: "normal",
    lineHeight: "100%",
    margin: "0px",
    marginRight: "8px",

    color: "#0C0E29",
  },

  accountCategory__title: {
    fontFamily: "Lato, sans-serif",
    fontSize: "18px",
    fontWeight: "normal",
    lineHeight: "100%",
    margin: "0px",
    minWidth: "244px",
    color: "#0C0E29",
  },

  accountCategory__total: {
    fontFamily: "Lato, sans-serif",
    fontSize: "18px",
    fontWeight: "normal",
    lineHeight: "100%",
    margin: "0px",

    color: "#0C0E29",
  },
  account_listItem: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  cornerDownRight: {
    width: "20px",
    height: "20px",
    color: "var(--color-primary)",
  },
  cornerWhiteDownRight: {
    color: "white !important",
  },
  accountCategory__accounts: {
    width: "100%",
    display: "flex",
    flexDirection: "column",

    // paddingLeft: "24px",
  },
  account__text: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    fontSize: "15px",
  },
  account: {
    height: "fit-content",
    boxSizing: "border-box",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",

    backgroundColor: "transparent !important",
    color: "#0C0E29",

    border: "none",
    borderRadius: "4px",

    padding: "4px 4px",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#F4F4F4",
      // cursor: "pointer",
    },
  },
  subdivision: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: "24px",
  },
  account__highlighted: {
    backgroundColor: "var(--color-primary) !important",
    color: "white !important",
    opacity: "1",

    "&:hover": {
      backgroundColor: "#a77d4f",
    },
  },

  account__title: {
    margin: "0px",
    fontSize: "14px",

    opacity: "0.6",
  },

  account__price: {
    margin: "0px",
    // paddingRight: '10px',
  },
  account__price_sm: {
    margin: "0px",
    width: "160px",
    fontSize: "14px",

    opacity: "0.6",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#F8F8F8",
      borderRadius: "4px",
      opacity: "1",
    },
  },

  account__percentage_sm: {
    margin: "0px",
    fontSize: "14px",
  },

  profitLossBalance: {
    paddingTop: "5px",
    // display: 'flex',
    // flexDirection: 'column',
    width: "100%",
    borderBottom: "1px solid rgb(237, 237, 237)",
  },

  revenueCostBlock: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: "16px",
  },
  vertical__chip__green: {
    width: "fit-content",
    textOrientation: "upright",
    display: "inline-flex",
    alignItems: "center",
    writingMode: "vertical-lr",
    padding: "0px 12px",
    margin: "0px",

    fontSize: "14px",
    fontWeight: "normal",
    fontFamily: "Lato, sans-serif",
    lineHeight: "100%",
    color: "#3AB037",

    borderRadius: "12px",
    backgroundColor: "#E2F8E0",
  },

  chip__green: {
    width: "fit-content",
    height: "24px",

    display: "inline-flex",
    alignItems: "center",

    padding: "0px 12px",
    margin: "0px",

    fontSize: "14px",
    fontWeight: "normal",
    fontFamily: "Lato, sans-serif",
    lineHeight: "100%",
    color: "#3AB037",

    borderRadius: "12px",
    backgroundColor: "#E2F8E0",
  },

  chip__red: {
    width: "fit-content",
    height: "24px",

    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    padding: "0px 12px",
    fontSize: "14px",
    fontWeight: "normal",
    fontFamily: "Lato, sans-serif",
    lineHeight: "100%",

    borderRadius: "12px",

    backgroundColor: "#FBE5E5",
    color: "#D84949",

    margin: "0px",
  },

  report__total: {
    width: "100%",
    height: "48px",
    backgroundColor: "var(--color-bg-card) !important",
    color: "var(--color-text-primary)",
  },
  compare__total: {
    display: "flex",
    flexDirection: "row",
  },

  total__title: {
    width: "160px",

    borderRight: "0px !important",
    gap: "8px",
    margin: "0px",

    fontFamily: "Lato, sans-serif",
    fontSize: "18px",
    fontWeight: "600",
    color: "inherit",

    whiteSpace: "nowrap",
  },

  total__breakdown: {
    fontSize: "14px",
    opacity: "0.8",

    margin: "0px",
  },

  total__price: {
    fontFamily: "Lato, sans-serif",
    fontSize: "18px",
    fontWeight: "normal",
    margin: "0px",

    color: "#0C0E29",
  },
}));
